import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';

const materialCache = new Map();
let toonGradient;

function getToonGradient() {
  if (toonGradient) return toonGradient;
  const values = [
    72, 72, 82, 255,
    132, 132, 142, 255,
    202, 202, 208, 255,
    255, 255, 255, 255,
  ];
  toonGradient = new THREE.DataTexture(new Uint8Array(values), 4, 1, THREE.RGBAFormat);
  toonGradient.minFilter = THREE.NearestFilter;
  toonGradient.magFilter = THREE.NearestFilter;
  toonGradient.needsUpdate = true;
  return toonGradient;
}

export function toon(color, options = {}) {
  const key = `${color}-${options.roughness ?? 1}-${options.transparent ?? false}-${options.opacity ?? 1}-${options.side ?? 0}`;
  if (!materialCache.has(key)) {
    materialCache.set(key, new THREE.MeshToonMaterial({
      color,
      gradientMap: getToonGradient(),
      transparent: options.transparent ?? false,
      opacity: options.opacity ?? 1,
      side: options.side ?? THREE.FrontSide,
    }));
  }
  return materialCache.get(key);
}

export function standard(color, options = {}) {
  return new THREE.MeshStandardMaterial({
    color,
    roughness: options.roughness ?? 0.86,
    metalness: options.metalness ?? 0,
    transparent: options.transparent ?? false,
    opacity: options.opacity ?? 1,
    side: options.side ?? THREE.FrontSide,
  });
}

export function roundedBox(width, height, depth, color, radius = 0.12, segments = 2) {
  const geometry = new RoundedBoxGeometry(width, height, depth, segments, Math.min(radius, width * .22, height * .22, depth * .22));
  const mesh = new THREE.Mesh(geometry, toon(color));
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

export function box(width, height, depth, color) {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), toon(color));
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

export function cylinder(radiusTop, radiusBottom, height, color, segments = 12) {
  const mesh = new THREE.Mesh(new THREE.CylinderGeometry(radiusTop, radiusBottom, height, segments), toon(color));
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

export function sphere(radius, color, widthSegments = 16, heightSegments = 10) {
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(radius, widthSegments, heightSegments), toon(color));
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

export function plane(width, depth, color) {
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(width, depth), toon(color, { side: THREE.DoubleSide }));
  mesh.rotation.x = -Math.PI / 2;
  mesh.receiveShadow = true;
  return mesh;
}

export function profilePrism(points, width, color, options = {}) {
  const shape = new THREE.Shape();
  points.forEach(([x, y], index) => {
    if (index === 0) shape.moveTo(x, y);
    else shape.lineTo(x, y);
  });
  shape.closePath();
  const bevel = options.bevel ?? .055;
  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth: width,
    steps: 1,
    bevelEnabled: bevel > 0,
    bevelSegments: options.bevelSegments ?? 2,
    bevelSize: bevel,
    bevelThickness: bevel,
    curveSegments: 2,
  });
  geometry.rotateY(Math.PI / 2);
  geometry.translate(-width / 2, options.y ?? 0, 0);
  geometry.computeVertexNormals();
  const mesh = new THREE.Mesh(geometry, toon(color));
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

export function tubeBetween(start, end, radius, color, segments = 8) {
  const direction = new THREE.Vector3().subVectors(end, start);
  const mesh = cylinder(radius, radius, direction.length(), color, segments);
  mesh.position.copy(start).add(end).multiplyScalar(.5);
  mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize());
  return mesh;
}

export function leaf(width, length, color, thickness = .035) {
  const shape = new THREE.Shape();
  shape.moveTo(0, -length / 2);
  shape.bezierCurveTo(width * .62, -length * .22, width * .62, length * .2, 0, length / 2);
  shape.bezierCurveTo(-width * .62, length * .2, -width * .62, -length * .22, 0, -length / 2);
  const geometry = new THREE.ExtrudeGeometry(shape, {
    depth: thickness,
    bevelEnabled: true,
    bevelSegments: 1,
    bevelSize: thickness * .45,
    bevelThickness: thickness * .45,
  });
  geometry.translate(0, 0, -thickness / 2);
  const mesh = new THREE.Mesh(geometry, toon(color));
  mesh.castShadow = true;
  return mesh;
}

export function irregularRock(radius, color, seed = 1) {
  const geometry = new THREE.IcosahedronGeometry(radius, 2);
  const position = geometry.attributes.position;
  for (let i = 0; i < position.count; i += 1) {
    const x = position.getX(i);
    const y = position.getY(i);
    const z = position.getZ(i);
    const wobble = .82 + ((Math.sin(x * 7.1 + seed) + Math.sin(y * 9.7 + seed * 2.3) + Math.cos(z * 8.3 - seed)) / 3 + 1) * .1;
    position.setXYZ(i, x * wobble, y * wobble, z * wobble);
  }
  geometry.computeVertexNormals();
  const mesh = new THREE.Mesh(geometry, toon(color));
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

export function addOutline(mesh, color = 0x211a2e, scale = 1.035) {
  if (!mesh.geometry) return null;
  const outline = new THREE.Mesh(mesh.geometry, new THREE.MeshBasicMaterial({ color, side: THREE.BackSide }));
  outline.scale.multiplyScalar(scale);
  outline.position.copy(mesh.position);
  outline.rotation.copy(mesh.rotation);
  return outline;
}

export function checkerTexture(colorA = '#e7b378', colorB = '#d79a61', cells = 12) {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 256;
  const ctx = canvas.getContext('2d');
  const size = canvas.width / cells;
  for (let y = 0; y < cells; y += 1) {
    for (let x = 0; x < cells; x += 1) {
      ctx.fillStyle = (x + y) % 2 ? colorA : colorB;
      ctx.fillRect(x * size, y * size, size + 1, size + 1);
    }
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  return texture;
}

export function plankTexture(colorA = '#d8a269', colorB = '#bd8151') {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 512;
  const ctx = canvas.getContext('2d');
  const plankHeight = 64;
  for (let row = 0; row < 8; row += 1) {
    const offset = row % 2 ? -128 : 0;
    for (let col = -1; col < 3; col += 1) {
      const x = offset + col * 256;
      ctx.fillStyle = (row + col) % 3 === 0 ? colorB : colorA;
      ctx.fillRect(x, row * plankHeight, 256, plankHeight);
      ctx.strokeStyle = 'rgba(73, 43, 35, .28)';
      ctx.lineWidth = 4;
      ctx.strokeRect(x, row * plankHeight, 256, plankHeight);
      ctx.strokeStyle = 'rgba(255, 255, 255, .08)';
      ctx.lineWidth = 2;
      for (let grain = 0; grain < 4; grain += 1) {
        const gy = row * plankHeight + 13 + grain * 11;
        ctx.beginPath();
        ctx.moveTo(x + 12, gy);
        ctx.bezierCurveTo(x + 70, gy - 7, x + 155, gy + 8, x + 242, gy - 2);
        ctx.stroke();
      }
    }
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 8;
  return texture;
}

export function speckleTexture(colorA = '#67a85b', colorB = '#4c8e4b', seed = 13) {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 256;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = colorA;
  ctx.fillRect(0, 0, 256, 256);
  const random = seededRandom(seed);
  for (let i = 0; i < 700; i += 1) {
    ctx.globalAlpha = .08 + random() * .16;
    ctx.fillStyle = random() > .45 ? colorB : '#eff6bf';
    const size = 1 + random() * 4;
    ctx.fillRect(random() * 256, random() * 256, size, size);
  }
  ctx.globalAlpha = 1;
  const texture = new THREE.CanvasTexture(canvas);
  texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 6;
  return texture;
}

export function seededRandom(seed = 1) {
  let value = seed % 2147483647;
  if (value <= 0) value += 2147483646;
  return () => {
    value = value * 16807 % 2147483647;
    return (value - 1) / 2147483646;
  };
}
